import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiUser, FiPhone, FiMail, FiMessageSquare, FiInbox, FiXCircle } from 'react-icons/fi';
import { FaWhatsapp, FaPhoneAlt } from 'react-icons/fa';
import './AdminPages.css';

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnquiries(res.data);
    } catch (err) {
      setError('Failed to load enquiries');
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    setUpdatingId(id);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/admin/enquiries/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Enquiry marked as ${status}`);
      fetchEnquiries();
    } catch (err) {
      toast.error('Failed to update enquiry');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      replied: { bg: '#d1fae5', color: '#065f46' },
      closed:  { bg: '#f1f5f9', color: '#475569' },
    };
    const s = map[status] || map.pending;
    return (
      <span style={{ padding: '0.25rem 0.625rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Enquiries</h1></div>
        <div className="admin-card"><div className="skeleton" style={{ height: '400px', borderRadius: '8px' }}></div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Enquiries</h1></div>
        <div className="admin-card empty-state">
          <FiXCircle size={40} />
          <h3>{error}</h3>
          <button className="btn-primary" onClick={fetchEnquiries}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Enquiries</h1>
          <p>{enquiries.length} total enquir{enquiries.length !== 1 ? 'ies' : 'y'}</p>
        </div>
      </div>

      <div className="admin-card table-wrapper">
        {enquiries.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Enquiries Yet</h3>
            <p>Enquiries from the frontend will appear here.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Property</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map(e => (
                  <tr key={e.id}>
                    <td><span className="hybrid-id">#{e.id}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <strong style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <FiUser size={13} /> {e.name}
                        </strong>
                        <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <FiPhone size={12} /> {e.phone}
                        </span>
                        {e.email && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                            <FiMail size={12} /> {e.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span className="info-pill" style={{ alignSelf: 'flex-start' }}>
                          Property #{e.property_id || 'N/A'}
                        </span>
                        {e.sub_register_name && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)' }}>
                            <strong>Sub-Register:</strong> {e.sub_register_name}
                          </span>
                        )}
                        {e.partner_name && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)' }}>
                            <strong>Partner:</strong> {e.partner_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <p style={{ fontSize: '0.8rem', color: 'var(--admin-text-main)', maxWidth: '200px', lineHeight: 1.4 }}>
                        {e.message ? (e.message.length > 60 ? e.message.substring(0, 60) + '...' : e.message) : <em style={{ color: 'var(--admin-text-muted)' }}>No message</em>}
                      </p>
                    </td>
                    <td>{getStatusBadge(e.status)}</td>
                    <td>
                      <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                        {new Date(e.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <a
                          href={`tel:${e.phone}`}
                          className="btn-icon"
                          title="Call"
                          style={{ color: '#2563eb', borderColor: '#bfdbfe', background: '#eff6ff' }}
                        >
                          <FaPhoneAlt size={13} />
                        </a>
                        <a
                          href={`https://wa.me/${e.phone?.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-icon"
                          title="WhatsApp"
                          style={{ color: '#16a34a', borderColor: '#bbf7d0', background: '#f0fdf4' }}
                        >
                          <FaWhatsapp size={15} />
                        </a>
                        <select
                          value={e.status}
                          disabled={updatingId === e.id}
                          onChange={(ev) => handleStatusUpdate(e.id, ev.target.value)}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--admin-border)' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="replied">Replied</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryList;
